import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Gauge,
  HeartPulse,
  Save,
  Stethoscope,
  Thermometer,
  TriangleAlert,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { withOwner } from "@/lib/supabase/withOwner";
import { toast } from "@/lib/safeToast";
import { mirrorVitalSignEvent } from "@/lib/clinical/eventPersistence";
import {
  assessVitalRecord,
  normalizeVitalRecord,
  numberOrNull,
  vitalContextText,
  vitalRecordSchema,
  vitalSummary,
  type VitalRedFlags,
} from "@/lib/clinical/vitalAssessment";

function dtNow() {
  const date = new Date();
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function fmt(value: any) {
  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function errorText(error: any) {
  return `${error?.code ?? ""} ${error?.message ?? ""} ${error?.details ?? ""}`;
}

function isSchemaCompatibilityError(error: any) {
  return /column|schema cache|PGRST204|42703/i.test(errorText(error));
}

function isMissingCanonicalVitalsTable(error: any) {
  return /42P01|relation .*vital_signs.* does not exist/i.test(errorText(error));
}

const EMPTY_FLAGS: VitalRedFlags = {
  chest_pain: false,
  dyspnea: false,
  syncope: false,
  confusion: false,
  seizure: false,
  unresponsive: false,
  new_neurologic_deficit: false,
  vision_change: false,
};

export function VitalSignsPanel({
  patientId,
  sessionId,
  compact = false,
  onSaved,
}: {
  patientId: string;
  sessionId?: string | null;
  compact?: boolean;
  onSaved?: (row: any) => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [showEntryForm, setShowEntryForm] = useState(!compact);
  const [f, setF] = useState<any>({
    measured_at: dtNow(),
    systolic_mm_hg: "",
    diastolic_mm_hg: "",
    heart_rate_bpm: "",
    spo2_percent: "",
    respiratory_rate_bpm: "",
    temperature_celsius: "",
    glucose_mg_dl: "",
    weight_kg: "",
    posture: "repouso",
    measurement_source: "desconhecido",
    sleep_hours_last_24h: "",
    recent_exertion: false,
    hot_environment: false,
    caffeine_near_measurement: false,
    symptoms_text: "",
    linked_dose_id: "",
    context: "",
    clinical_note: "",
  });
  const [redFlags, setRedFlags] = useState<VitalRedFlags>({ ...EMPTY_FLAGS });

  useEffect(() => {
    setF((current: any) => ({
      ...current,
      measured_at: dtNow(),
      systolic_mm_hg: "",
      diastolic_mm_hg: "",
      heart_rate_bpm: "",
      spo2_percent: "",
      respiratory_rate_bpm: "",
      temperature_celsius: "",
      glucose_mg_dl: "",
      weight_kg: "",
      context: "",
      clinical_note: "",
      symptoms_text: "",
      linked_dose_id: "",
    }));
    setRedFlags({ ...EMPTY_FLAGS });
  }, [patientId]);

  const { data: rawRows = [] } = useQuery({
    queryKey: ["vital-signs", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const [canonical, legacy] = await Promise.all([
        (supabase as any)
          .from("vital_signs")
          .select("*")
          .eq("patient_id", patientId)
          .order("measured_at", { ascending: false })
          .limit(120),
        (supabase as any)
          .from("patient_vital_signs")
          .select("*")
          .eq("patient_id", patientId)
          .order("measured_at", { ascending: false })
          .limit(120),
      ]);
      if (canonical.error && legacy.error) throw canonical.error;
      return [
        ...((canonical.data ?? []) as any[])
          .filter((row) => !row.deleted_at)
          .map((row) => ({ ...row, __source_table: "vital_signs" })),
        ...((legacy.data ?? []) as any[])
          .filter((row) => !row.deleted_at)
          .map((row) => ({ ...row, __source_table: "patient_vital_signs" })),
      ].sort((a, b) => new Date(b.measured_at).getTime() - new Date(a.measured_at).getTime());
    },
  });

  const rows = useMemo(() => {
    const seen = new Set<string>();
    return (rawRows as any[])
      .map((row) => normalizeVitalRecord(row))
      .filter((row) => {
        const key = row.id ? `${row.__source_table}:${row.id}` : `${row.measured_at}:${row.heart_rate_bpm ?? ""}:${row.systolic_mm_hg ?? ""}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [rawRows]);
  const summary = useMemo(() => vitalSummary(rows), [rows]);
  const latestAlerts = useMemo(
    () => (summary.latest ? assessVitalRecord(summary.latest) : []),
    [summary.latest],
  );

  async function save() {
    if (saving) return;
    if (!user) return toast.error("Não autenticado.");
    if (!patientId) return toast.error("Selecione um paciente.");

    const contextStructured = {
      posture: f.posture || "desconhecido",
      measurement_source: f.measurement_source || "desconhecido",
      sleep_hours_last_24h: numberOrNull(f.sleep_hours_last_24h),
      recent_exertion: Boolean(f.recent_exertion),
      hot_environment: Boolean(f.hot_environment),
      caffeine_near_measurement: Boolean(f.caffeine_near_measurement),
      symptoms_text: f.symptoms_text?.trim() || null,
    };
    const contextText = [f.context?.trim(), vitalContextText(contextStructured as any, redFlags)]
      .filter(Boolean)
      .join(" · ");

    let measuredAt: string;
    try {
      measuredAt = new Date(f.measured_at).toISOString();
    } catch {
      toast.error("Data/hora inválida.");
      return;
    }

    const clinical = {
      measured_at: measuredAt,
      systolic_mm_hg: numberOrNull(f.systolic_mm_hg),
      diastolic_mm_hg: numberOrNull(f.diastolic_mm_hg),
      heart_rate_bpm: numberOrNull(f.heart_rate_bpm),
      spo2_percent: numberOrNull(f.spo2_percent),
      respiratory_rate_bpm: numberOrNull(f.respiratory_rate_bpm),
      temperature_celsius: numberOrNull(f.temperature_celsius),
      glucose_mg_dl: numberOrNull(f.glucose_mg_dl),
      weight_kg: numberOrNull(f.weight_kg),
      posture: f.posture || null,
      measurement_source: f.measurement_source || null,
      context: contextText || null,
      clinical_note: f.clinical_note?.trim() || null,
      linked_dose_id: f.linked_dose_id || null,
      context_structured: contextStructured,
      red_flags: redFlags,
    };

    const parsed = vitalRecordSchema.safeParse(clinical);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Confira os sinais vitais informados.");
      return;
    }

    setSaving(true);
    try {
      const basePayload = withOwner(
        {
          patient_id: patientId,
          session_id: sessionId ?? null,
          measured_at: measuredAt,
          systolic_mm_hg: clinical.systolic_mm_hg,
          diastolic_mm_hg: clinical.diastolic_mm_hg,
          heart_rate_bpm: clinical.heart_rate_bpm,
          spo2_percent: clinical.spo2_percent,
          temperature_celsius: clinical.temperature_celsius,
          glucose_mg_dl: clinical.glucose_mg_dl,
          posture: clinical.posture,
          context: clinical.context,
          clinical_note: clinical.clinical_note,
        },
        user,
      );
      const extendedPayload = {
        ...basePayload,
        respiratory_rate_bpm: clinical.respiratory_rate_bpm,
        weight_kg: clinical.weight_kg,
        measurement_source: clinical.measurement_source,
        context_structured: clinical.context_structured,
        red_flags: clinical.red_flags,
        linked_dose_id: clinical.linked_dose_id,
      };

      let sourceTable: "vital_signs" | "patient_vital_signs" = "vital_signs";
      let result = await (supabase as any)
        .from("vital_signs")
        .insert(extendedPayload)
        .select("*")
        .single();

      // Compatibilidade com bancos que ainda não receberam todos os campos novos.
      if (result.error && isSchemaCompatibilityError(result.error)) {
        console.warn("[VitalSignsPanel] extended vital schema unavailable; saving core fields", result.error);
        result = await (supabase as any)
          .from("vital_signs")
          .insert(basePayload)
          .select("*")
          .single();
      }

      // Último fallback para instalações antigas: salva na tabela legada em vez de perder PA/FC.
      if (result.error && isMissingCanonicalVitalsTable(result.error)) {
        sourceTable = "patient_vital_signs";
        const legacyPayload = withOwner(
          {
            patient_id: patientId,
            measured_at: measuredAt,
            systolic_bp: clinical.systolic_mm_hg,
            diastolic_bp: clinical.diastolic_mm_hg,
            heart_rate: clinical.heart_rate_bpm,
            oxygen_saturation: clinical.spo2_percent,
            weight_kg: clinical.weight_kg,
            context: clinical.context,
            linked_dose_id: clinical.linked_dose_id,
            notes: clinical.clinical_note,
          },
          user,
        );
        result = await (supabase as any)
          .from("patient_vital_signs")
          .insert(legacyPayload)
          .select("*")
          .single();
      }

      if (result.error) throw result.error;

      const saved = {
        ...result.data,
        ...clinical,
        id: result.data?.id,
        patient_id: patientId,
        created_at: result.data?.created_at,
        __source_table: sourceTable,
      };

      try {
        await mirrorVitalSignEvent(user, saved, sourceTable);
      } catch (mirrorError) {
        console.warn("[VitalSignsPanel] timeline mirror failed", mirrorError);
      }

      const alerts = assessVitalRecord(saved);
      toast.success(
        alerts.some((alert) => alert.level === "urgent")
          ? "Sinais vitais registrados. Há sinal de alerta clínico no registro."
          : "Sinais vitais registrados.",
      );

      setF((current: any) => ({
        ...current,
        measured_at: dtNow(),
        systolic_mm_hg: "",
        diastolic_mm_hg: "",
        heart_rate_bpm: "",
        spo2_percent: "",
        respiratory_rate_bpm: "",
        temperature_celsius: "",
        glucose_mg_dl: "",
        weight_kg: "",
        sleep_hours_last_24h: "",
        recent_exertion: false,
        hot_environment: false,
        caffeine_near_measurement: false,
        symptoms_text: "",
        linked_dose_id: "",
        context: "",
        clinical_note: "",
      }));
      setRedFlags({ ...EMPTY_FLAGS });

      await Promise.all([
        qc.invalidateQueries({ queryKey: ["vital-signs", patientId] }),
        qc.invalidateQueries({ queryKey: ["journal-vitals", patientId] }),
        qc.invalidateQueries({ queryKey: ["psylog-vitals", patientId] }),
        qc.invalidateQueries({ queryKey: ["my-space-vitals", patientId] }),
        qc.invalidateQueries({ queryKey: ["psylog-events", patientId] }),
      ]);
      onSaved?.(saved);
    } catch (error: any) {
      console.error("[VitalSignsPanel] save failed", error);
      toast.error(error?.message ?? "Falha ao registrar sinais vitais.");
    } finally {
      setSaving(false);
    }
  }

  const chartData = [...rows]
    .reverse()
    .slice(-60)
    .map((row) => ({
      t: new Date(row.measured_at).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
      FC: row.heart_rate_bpm ?? null,
      PAS: row.systolic_mm_hg ?? null,
      PAD: row.diastolic_mm_hg ?? null,
      SpO2: row.spo2_percent ?? null,
    }));

  return (
    <Card className="min-w-0 space-y-4 overflow-hidden border-primary/20 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium">
            <HeartPulse className="h-4 w-4 text-primary" /> Sinais vitais / FC
          </div>
          <div className="mt-1 max-w-3xl text-xs leading-relaxed text-muted-foreground">
            Registro longitudinal de FC, PA, SpO₂, FR e temperatura. Contexto da medida é preservado
            para evitar interpretar ansiedade, esforço ou fármaco como causalidade automática.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px]">
            {rows.length} registro(s)
          </Badge>
          {compact ? (
            <Button type="button" variant={showEntryForm ? "secondary" : "outline"} size="sm" onClick={() => setShowEntryForm((value) => !value)}>
              <HeartPulse className="mr-1 h-3.5 w-3.5" />
              {showEntryForm ? "Fechar registro" : "Registrar PA/FC"}
            </Button>
          ) : null}
        </div>
      </div>

      {summary.latest ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Metric label="FC mais recente" value={summary.latest.heart_rate_bpm != null ? `${summary.latest.heart_rate_bpm} bpm` : "—"} icon={<HeartPulse className="h-4 w-4" />} />
          <Metric
            label="PA mais recente"
            value={
              summary.latest.systolic_mm_hg != null && summary.latest.diastolic_mm_hg != null
                ? `${summary.latest.systolic_mm_hg}/${summary.latest.diastolic_mm_hg}`
                : "—"
            }
            icon={<Stethoscope className="h-4 w-4" />}
          />
          <Metric label="SpO₂ mais recente" value={summary.latest.spo2_percent != null ? `${summary.latest.spo2_percent}%` : "—"} icon={<Gauge className="h-4 w-4" />} />
          <Metric
            label="Mediana FC repouso"
            value={summary.restingHeartRateMedian != null ? `${Math.round(summary.restingHeartRateMedian)} bpm` : "—"}
            icon={<Activity className="h-4 w-4" />}
          />
        </div>
      ) : null}

      {latestAlerts.length ? (
        <div className="space-y-2 rounded-xl border border-warning/40 bg-warning/5 p-3">
          <div className="flex items-start gap-2">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div>
              <div className="text-sm font-medium">Última medida: pontos de atenção</div>
              <p className="text-xs text-muted-foreground">
                Triagem contextual; sintomas e qualidade técnica da medida prevalecem sobre um número isolado.
              </p>
            </div>
          </div>
          {latestAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`rounded-lg border p-2.5 text-xs ${alert.level === "urgent" ? "border-destructive/50 bg-destructive/5" : "border-warning/30"}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <strong>{alert.title}</strong>
                <Badge variant={alert.level === "urgent" ? "destructive" : "outline"}>
                  {alert.level === "urgent" ? "urgente" : "atenção"}
                </Badge>
              </div>
              <p className="mt-1 leading-relaxed text-muted-foreground">{alert.detail}</p>
              {alert.sourceUrl ? (
                <a className="mt-1 inline-block text-primary underline-offset-2 hover:underline" href={alert.sourceUrl} target="_blank" rel="noreferrer">
                  {alert.sourceLabel}
                </a>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {showEntryForm ? (
        <>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
            <Field label="Data/hora">
              <Input type="datetime-local" value={f.measured_at} onChange={(event) => setF({ ...f, measured_at: event.target.value })} />
            </Field>
            <Field label="FC · bpm" emphasis>
              <Input type="number" inputMode="numeric" placeholder="78" value={f.heart_rate_bpm} onChange={(event) => setF({ ...f, heart_rate_bpm: event.target.value })} />
            </Field>
            <Field label="PAS · mmHg">
              <Input type="number" inputMode="numeric" placeholder="120" value={f.systolic_mm_hg} onChange={(event) => setF({ ...f, systolic_mm_hg: event.target.value })} />
            </Field>
            <Field label="PAD · mmHg">
              <Input type="number" inputMode="numeric" placeholder="80" value={f.diastolic_mm_hg} onChange={(event) => setF({ ...f, diastolic_mm_hg: event.target.value })} />
            </Field>
            <Field label="SpO₂ · %">
              <Input type="number" inputMode="decimal" step="0.1" placeholder="98" value={f.spo2_percent} onChange={(event) => setF({ ...f, spo2_percent: event.target.value })} />
            </Field>
            <Field label="FR · irpm">
              <Input type="number" inputMode="numeric" placeholder="16" value={f.respiratory_rate_bpm} onChange={(event) => setF({ ...f, respiratory_rate_bpm: event.target.value })} />
            </Field>
            <Field label="Temperatura · °C">
              <Input type="number" inputMode="decimal" step="0.1" placeholder="36,6" value={f.temperature_celsius} onChange={(event) => setF({ ...f, temperature_celsius: event.target.value })} />
            </Field>
            <Field label="Glicemia · mg/dL">
              <Input type="number" inputMode="decimal" value={f.glucose_mg_dl} onChange={(event) => setF({ ...f, glucose_mg_dl: event.target.value })} />
            </Field>
            <Field label="Peso · kg">
              <Input type="number" inputMode="decimal" step="0.1" value={f.weight_kg} onChange={(event) => setF({ ...f, weight_kg: event.target.value })} />
            </Field>
            <Field label="Condição da medida">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={f.posture}
                onChange={(event) => setF({ ...f, posture: event.target.value })}
              >
                <option value="repouso">Repouso</option>
                <option value="sentado">Sentado</option>
                <option value="em_pe">Em pé</option>
                <option value="atividade">Durante atividade</option>
                <option value="pos_atividade">Pós-atividade</option>
                <option value="desconhecido">Não informado</option>
              </select>
            </Field>
            <Field label="Fonte / dispositivo">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={f.measurement_source}
                onChange={(event) => setF({ ...f, measurement_source: event.target.value })}
              >
                <option value="desconhecido">Não informado</option>
                <option value="manual">Manual</option>
                <option value="relogio">Relógio / wearable</option>
                <option value="oximetro">Oxímetro</option>
                <option value="esfigmomanometro">Esfigmomanômetro</option>
                <option value="termometro">Termômetro</option>
                <option value="outro">Outro</option>
              </select>
            </Field>
            <Field label="Sono últimas 24 h">
              <Input type="number" inputMode="decimal" min="0" max="24" step="0.5" placeholder="horas" value={f.sleep_hours_last_24h} onChange={(event) => setF({ ...f, sleep_hours_last_24h: event.target.value })} />
            </Field>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              ["recent_exertion", "Esforço físico recente"],
              ["hot_environment", "Ambiente quente"],
              ["caffeine_near_measurement", "Cafeína próxima"],
            ].map(([key, label]) => (
              <label key={key} className="flex min-h-10 items-center gap-2 rounded-lg border px-3 text-xs">
                <input
                  type="checkbox"
                  checked={Boolean(f[key])}
                  onChange={(event) => setF({ ...f, [key]: event.target.checked })}
                />
                {label}
              </label>
            ))}
          </div>

          <div className="space-y-2 rounded-xl border border-destructive/20 p-3">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sinais de alerta presentes</div>
              <p className="mt-1 text-xs text-muted-foreground">Marque somente fenômenos observados no momento da medida.</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["chest_pain", "Dor no peito"],
                ["dyspnea", "Dispneia importante"],
                ["syncope", "Síncope / desmaio"],
                ["confusion", "Confusão aguda"],
                ["seizure", "Convulsão"],
                ["unresponsive", "Não responsivo / difícil despertar"],
                ["new_neurologic_deficit", "Déficit neurológico novo"],
                ["vision_change", "Alteração visual aguda"],
              ].map(([key, label]) => (
                <label key={key} className="flex min-h-10 items-center gap-2 rounded-lg border px-3 text-xs">
                  <input
                    type="checkbox"
                    checked={Boolean(redFlags[key as keyof VitalRedFlags])}
                    onChange={(event) => setRedFlags((currentFlags) => ({ ...currentFlags, [key]: event.target.checked }))}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Sintomas / contexto percebido">
              <Input
                placeholder="palpitações, tremor, ansiedade, sonolência…"
                value={f.symptoms_text}
                onChange={(event) => setF({ ...f, symptoms_text: event.target.value })}
              />
            </Field>
            <Field label="Contexto clínico livre">
              <Input
                placeholder="ex.: 2 h após LDX; após clonidina; pós-esforço"
                value={f.context}
                onChange={(event) => setF({ ...f, context: event.target.value })}
              />
            </Field>
          </div>

          <Field label="Nota clínica">
            <Textarea
              rows={2}
              value={f.clinical_note}
              onChange={(event) => setF({ ...f, clinical_note: event.target.value })}
              placeholder="Interpretação, qualidade da medida, conduta ou ressalva."
            />
          </Field>

          <div className="flex justify-end">
            <Button type="button" onClick={save} disabled={saving} className="w-full sm:w-auto">
              <Save className="mr-1.5 h-4 w-4" />
              {saving ? "Salvando…" : "Registrar sinais vitais"}
            </Button>
          </div>
        </>
      ) : null}

      {rows.length ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="h-52 rounded-lg border bg-muted/20 p-2">
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="t" stroke="hsl(var(--muted-foreground))" fontSize={10} minTickGap={20} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} width={34} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Line type="monotone" dataKey="FC" stroke="hsl(var(--primary))" strokeWidth={2.4} dot={false} connectNulls={false} />
                <Line type="monotone" dataKey="PAS" stroke="hsl(var(--warning))" strokeWidth={1.5} dot={false} connectNulls={false} />
                <Line type="monotone" dataKey="PAD" stroke="hsl(var(--muted-foreground))" strokeWidth={1.3} dot={false} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="max-h-52 space-y-2 overflow-auto pr-1">
            {rows.slice(0, compact ? 5 : 12).map((row) => {
              const alerts = assessVitalRecord(row);
              const urgent = alerts.some((alert) => alert.level === "urgent");
              const attention = alerts.length > 0;
              return (
                <div key={row.id ?? row.measured_at} className="rounded-md border bg-card p-2.5 text-xs">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 font-medium">
                      <Activity className="h-3.5 w-3.5" />
                      {fmt(row.measured_at)}
                    </div>
                    <Badge
                      variant={urgent ? "destructive" : "outline"}
                      className={!urgent && attention ? "border-warning text-warning" : ""}
                    >
                      {urgent ? "urgente" : attention ? "atenção" : "contextual"}
                    </Badge>
                  </div>
                  <div className="mt-1 leading-relaxed text-muted-foreground">
                    FC {row.heart_rate_bpm ?? "—"} bpm · PA {row.systolic_mm_hg ?? "—"}/{row.diastolic_mm_hg ?? "—"} ·
                    SpO₂ {row.spo2_percent ?? "—"}% · FR {row.respiratory_rate_bpm ?? "—"} ·{" "}
                    <Thermometer className="inline h-3 w-3" /> {row.temperature_celsius ?? "—"}°C
                  </div>
                  {(row.context || row.clinical_note) ? (
                    <div className="mt-1 break-words">{[row.context, row.clinical_note].filter(Boolean).join(" — ")}</div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          Sem sinais vitais registrados. FC/PA seriadas, em contexto de repouso e horário conhecido,
          são particularmente úteis para avaliar efeitos autonômicos e tolerabilidade longitudinal.
        </div>
      )}

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        O painel não atribui causalidade a um fármaco ou substância apenas por proximidade temporal.
        Para FC fora da faixa usual de repouso, o contexto e os sintomas determinam relevância clínica.
      </p>
    </Card>
  );
}

function Metric({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="min-w-0 rounded-xl border bg-muted/20 p-3">
      <div className="flex items-center gap-1.5 text-muted-foreground">{icon}<span className="text-[10px]">{label}</span></div>
      <div className="mt-1 truncate text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Field({
  label,
  children,
  emphasis = false,
}: {
  label: string;
  children: React.ReactNode;
  emphasis?: boolean;
}) {
  return (
    <div className={`min-w-0 space-y-1.5 ${emphasis ? "rounded-lg border border-primary/30 bg-primary/5 p-2" : ""}`}>
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
